/**
 * Global Number & Currency Formatting Utilities
 *
 * Centralized formatting functions for German locale (de-DE).
 * Use these functions consistently across the application.
 *
 * Examples:
 *   formatNumber(12345)        -> "12.345"
 *   formatCurrency(1234.56)    -> "1.234,56 €"
 *   formatDate("2026-01-15")   -> "15.01.2026"
 *   formatDateTime("2026-01-15T10:30:00Z") -> "15. Jan. 2026, 10:30"
 *   formatPoints(12345)        -> "12.345 Punkte"
 *   formatHectares(12.5)       -> "12,5 ha"
 */

const LOCALE = "de-DE";

/**
 * Format a number with German thousand separators.
 *
 * @example formatNumber(12345) -> "12.345"
 * @example formatNumber(1234.5) -> "1.234,5"
 * @example formatNumber(null) -> "–"
 */
export function formatNumber(
  value: number | string | null | undefined,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  if (value == null) return "–";
  // Accept strings defensively. App reads come back as JS numbers via PostgREST,
  // but raw node-pg / the Supabase management API return `numeric` as a string —
  // and String.prototype.toLocaleString would return that string unformatted
  // (ignoring locale/options, so "50000" renders ungrouped). Coerce so
  // toLocaleString always formats. 869djfvhc.
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return "–";
  return n.toLocaleString(LOCALE, options);
}

/**
 * Format a currency value in Euros with German formatting.
 *
 * @example formatCurrency(1234.56) -> "1.234,56 €"
 * @example formatCurrency(1234) -> "1.234,00 €"
 * @example formatCurrency(null) -> "–"
 */
export function formatCurrency(
  value: number | null | undefined,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    showSymbol?: boolean;
  }
): string {
  if (value == null) return "–";

  const { minimumFractionDigits = 2, maximumFractionDigits = 2, showSymbol = true } = options ?? {};

  const formatted = value.toLocaleString(LOCALE, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return showSymbol ? `${formatted} €` : formatted;
}

/**
 * Format a date string in German locale (date only).
 *
 * @example formatDate("2026-01-15T10:30:00Z") -> "15.01.2026"
 * @example formatDate(null) -> "–"
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return "–";
  return new Date(value).toLocaleDateString(LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/**
 * Format a date string in German locale with time.
 *
 * @example formatDateTime("2026-01-15T10:30:00Z") -> "15. Jan. 2026, 10:30"
 * @example formatDateTime(null) -> "–"
 */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return "–";
  return new Date(value).toLocaleDateString(LOCALE, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Format a date+time as `dd.mm.yyyy, hh:mm Uhr` in the Europe/Berlin
 * timezone. Use this in emails and other surfaces that quote a moment to
 * a German-speaking user — Vercel/Lambda runtimes default to UTC, so the
 * timezone has to be pinned explicitly to avoid showing UTC times.
 *
 * @example formatBerlinDateTime("2026-04-24T13:42:00Z") -> "24.04.2026, 15:42 Uhr"
 */
export function formatBerlinDateTime(value: string | Date | null | undefined): string {
  if (!value) return "–";
  const d = value instanceof Date ? value : new Date(value);
  const date = d.toLocaleDateString(LOCALE, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Europe/Berlin",
  });
  const time = d.toLocaleTimeString(LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Berlin",
  });
  return `${date}, ${time} Uhr`;
}

/**
 * Format a date string in German locale with full month name.
 *
 * @example formatDateLong("2026-01-15T10:30:00Z") -> "15. Januar 2026"
 * @example formatDateLong(null) -> "–"
 */
export function formatDateLong(value: string | null | undefined): string {
  if (!value) return "–";
  return new Date(value).toLocaleDateString(LOCALE, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/**
 * Format a date as a relative "days ago" string in German.
 *
 * @example formatDaysAgo("2026-03-28") -> "Heute" (if today is 2026-03-28)
 * @example formatDaysAgo("2026-03-27") -> "1 Tag" (if today is 2026-03-28)
 * @example formatDaysAgo("2026-03-25") -> "3 Tage" (if today is 2026-03-28)
 * @example formatDaysAgo(null) -> "–"
 */
export function formatDaysAgo(value: string | null | undefined): string {
  if (!value) return "–";
  const days = Math.floor((Date.now() - new Date(value).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return "Heute";
  if (days === 1) return "1 Tag";
  return `${days} Tage`;
}

/**
 * Format Ökopunkte with thousand separators.
 *
 * @example formatPoints(12345) -> "12.345 Punkte"
 * @example formatPoints(1) -> "1 Punkt"
 * @example formatPoints(12345, { short: true }) -> "12.345"
 */
export function formatPoints(
  value: number | null | undefined,
  options?: {
    short?: boolean;
  }
): string {
  if (value == null) return "–";

  const formatted = value.toLocaleString(LOCALE);

  if (options?.short) return formatted;

  const unit = value === 1 ? "Punkt" : "Punkte";
  return `${formatted} ${unit}`;
}

/**
 * Format hectares with German decimal formatting.
 *
 * @example formatHectares(12.5) -> "12,5 ha"
 * @example formatHectares(1234.567, { precision: 1 }) -> "1.234,6 ha"
 */
export function formatHectares(
  value: number | null | undefined,
  options?: {
    precision?: number;
    short?: boolean;
  }
): string {
  if (value == null) return "–";

  const { precision = 2, short = false } = options ?? {};

  const formatted = value.toLocaleString(LOCALE, {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });

  return short ? formatted : `${formatted} ha`;
}

/**
 * A distance or a radius, given in metres, German. One rule for both so the
 * Artenschutz pair reads consistently (ARCH-039 P57): metres below a
 * kilometre, kilometres above it with at most one decimal. Sales states a
 * radius IN METRES, so rendering 500 m as "0,5 km" would read as a figure
 * nobody entered.
 *
 * @example formatDistance(850) -> "850 m"
 * @example formatDistance(500) -> "500 m"
 * @example formatDistance(2000) -> "2 km"
 * @example formatDistance(12300) -> "12,3 km"
 */
export function formatDistance(meters: number): string {
  if (!Number.isFinite(meters)) return "–";
  const wholeMeters = Math.round(meters);
  if (Math.abs(wholeMeters) < 1000) return `${formatNumber(wholeMeters)} m`;
  return `${formatNumber(meters / 1000, { maximumFractionDigits: 1 })} km`;
}

/**
 * Quantity unit word per product, German. Single source of truth for the
 * inline `type === "okopunkte" ? "Punkte" : "Hektar"` duplications across the
 * Angebot/Anfrage cards and the ÖPaaS request flow (ClickUp 869dj6r1p).
 *
 * Only the two products selectable in the ÖPaaS request flow are mapped;
 * `getQuantityUnit` returns "" for anything else so legacy/other-product rows
 * render the bare number (their pre-existing behaviour).
 */
export const QUANTITY_UNIT_LABELS: Record<string, string> = {
  okopunkte: "Punkte",
  ausgleichsflaechen: "Hektar",
};

/**
 * Resolve the quantity unit word for a product `type`.
 *
 * @example getQuantityUnit("okopunkte") -> "Punkte"
 * @example getQuantityUnit("ausgleichsflaechen") -> "Hektar"
 * @example getQuantityUnit("co2_zertifikate") -> ""
 */
export function getQuantityUnit(type: string | null | undefined): string {
  if (!type) return "";
  return QUANTITY_UNIT_LABELS[type] ?? "";
}

/**
 * Format a quantity with its product unit (German locale). Falls back to the
 * bare number for products without a mapped unit (legacy/other rows), so there
 * is never a dangling space. Callers handle the null case themselves (e.g. "-").
 *
 * @example formatQuantityWithUnit(50000, "okopunkte") -> "50.000 Punkte"
 * @example formatQuantityWithUnit(3.5, "ausgleichsflaechen") -> "3,5 Hektar"
 * @example formatQuantityWithUnit(1000, "co2_zertifikate") -> "1.000"
 */
export function formatQuantityWithUnit(value: number, type: string | null | undefined): string {
  const formatted = value.toLocaleString(LOCALE);
  const unit = getQuantityUnit(type);
  return unit ? `${formatted} ${unit}` : formatted;
}

/**
 * Format a price per unit (e.g., €/Punkt, €/ha).
 *
 * @example formatPricePerUnit(2.5, "Punkt") -> "2,50 €/Punkt"
 * @example formatPricePerUnit(100, "ha") -> "100,00 €/ha"
 */
export function formatPricePerUnit(
  value: number | null | undefined,
  unit: string,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  }
): string {
  if (value == null) return "–";

  const { minimumFractionDigits = 2, maximumFractionDigits = 2 } = options ?? {};

  const formatted = value.toLocaleString(LOCALE, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatted} €/${unit}`;
}

/**
 * Parse a German-formatted number string back to a number.
 * Handles thousand separators (.) and decimal separators (,).
 *
 * @example parseGermanNumber("12.345,67") -> 12345.67
 * @example parseGermanNumber("1.234") -> 1234
 * @example parseGermanNumber("invalid") -> NaN
 */
export function parseGermanNumber(value: string): number {
  if (!value) return NaN;

  // Remove thousand separators (.) and replace decimal separator (,) with (.)
  const normalized = value.replace(/\./g, "").replace(",", ".");

  return parseFloat(normalized);
}

/**
 * Format a number for display in an input field.
 * Adds thousand separators while preserving the decimal part during editing.
 *
 * @example formatInputNumber("12345") -> "12.345"
 * @example formatInputNumber("12345,67") -> "12.345,67"
 * @example formatInputNumber("12.345,67") -> "12.345,67" (already formatted)
 */
export function formatInputNumber(value: string): string {
  if (!value) return "";

  // Split into integer and decimal parts
  const parts = value.replace(/\./g, "").split(",");
  const integerPart = parts[0] || "";
  const decimalPart = parts[1];

  // Format integer part with thousand separators
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

  // Reconstruct with decimal part if present
  if (decimalPart !== undefined) {
    return `${formattedInteger},${decimalPart}`;
  }

  return formattedInteger;
}

/**
 * Format a number as compact representation.
 * Useful for displaying large numbers in limited space.
 *
 * @example formatCompactNumber(1234) -> "1.234"
 * @example formatCompactNumber(12345) -> "12K"
 * @example formatCompactNumber(1234567) -> "1,2M"
 */
export function formatCompactNumber(value: number | null | undefined): string {
  if (value == null) return "–";

  if (value < 10000) {
    return value.toLocaleString(LOCALE);
  }

  return new Intl.NumberFormat(LOCALE, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a range of values.
 *
 * @example formatRange(100, 500, formatNumber) -> "100 – 500"
 * @example formatRange(1.5, 3.0, (v) => formatCurrency(v)) -> "1,50 € – 3,00 €"
 */
/** Parse a German-formatted quantity string to a number.
 * Strips non-numeric chars for integers, converts comma decimal separator for floats. */
export function parseGermanQuantity(value: string, mode: "integer" | "decimal"): number {
  if (!value) return 0;
  if (mode === "integer") {
    return parseInt(value.replace(/\D/g, ""), 10) || 0;
  }
  return parseFloat(value.replace(/,/g, ".")) || 0;
}

/**
 * Parse a German-formatted currency/amount string to a number, correctly
 * handling thousands separators (".") and the decimal comma (",").
 *
 * Unlike `parseGermanQuantity(value, "decimal")` — which only swaps "," for "."
 * and therefore mis-reads "1.250,00" as 1.25 (parseFloat stops at the 2nd dot) —
 * this strips grouping dots first:
 *   "1.250,00"     -> 1250
 *   "950,50"       -> 950.5
 *   "1.000.000,99" -> 1000000.99
 *   "€ 1.250,00"   -> 1250
 *   "5"            -> 5
 *   ""             -> 0
 * Use for currency inputs that are NOT pre-filtered by filterQuantityInput
 * (e.g. the admin invoice amount field), where users naturally type grouping dots.
 */
export function parseGermanCurrency(value: string): number {
  return parseGermanDecimal(value);
}

/**
 * Parse a German-formatted decimal (grouping "." + decimal ",") to a number,
 * returning 0 for anything unparseable. Identical semantics to
 * {@link parseGermanCurrency} — the currency name is kept for the money call
 * sites; use this one for non-money decimals (hectares, quantities).
 *
 * ALWAYS prefer this over `parseGermanQuantity(v, "decimal")` for a value a
 * user typed: that helper only swaps "," for "." and therefore reads the
 * grouping dot in "15.000" as a decimal point (-> 15).
 *
 *   "1.250,00" -> 1250      "15.000" -> 15000
 *   "950,50"   -> 950.5     "3,5"    -> 3.5
 *   "€ 1.250"  -> 1250      ""       -> 0
 */
export function parseGermanDecimal(value: string): number {
  if (!value) return 0;
  const normalized = value
    .replace(/\./g, "") // strip thousands separators
    .replace(",", ".") // decimal comma -> dot
    .replace(/[^\d.-]/g, ""); // drop currency symbols / spaces
  return parseFloat(normalized) || 0;
}

/**
 * Live filter for quantity inputs. Returns the raw value with disallowed
 * characters stripped so a controlled `<input>` never holds garbage like
 * "5,5a". Mirrors the funnel's QuantityStep behaviour.
 *
 * - integer mode: digits only.
 * - decimal mode: digits + at most one comma (dot is treated as comma).
 */
export function filterQuantityInput(raw: string, mode: "integer" | "decimal"): string {
  if (mode === "integer") return raw.replace(/\D/g, "");
  const unified = raw.replace(/\./g, ",");
  const stripped = unified.replace(/[^\d,]/g, "");
  const firstComma = stripped.indexOf(",");
  if (firstComma === -1) return stripped;
  return stripped.slice(0, firstComma) + "," + stripped.slice(firstComma + 1).replace(/,/g, "");
}

/**
 * Live-format a quantity input as a German-grouped number while typing, so a
 * value can carry BOTH a thousand separator and a decimal — e.g. "1.234,5".
 *
 * Unlike {@link filterQuantityInput} (which treats "." as a decimal and so
 * mangles "1.234,5" into "1,2345"), this follows German convention: "." is a
 * grouping separator (re-derived by {@link formatInputNumber}) and "," is the
 * decimal. Parse the result with {@link parseGermanNumber}, NOT
 * `parseGermanQuantity(…, "decimal")` (which can't strip grouping dots).
 *
 * - integer mode: digits only, thousand-grouped → "1.234.567"
 * - decimal mode: digits + the first "," as decimal, thousand-grouped → "1.234,5"
 *
 * @example formatGermanQuantityInput("1234567", "integer") -> "1.234.567"
 * @example formatGermanQuantityInput("1.234,5", "decimal") -> "1.234,5"
 * @example formatGermanQuantityInput("12000,25", "decimal") -> "12.000,25"
 * @example formatGermanQuantityInput("", "integer") -> ""
 */
export function formatGermanQuantityInput(raw: string, mode: "integer" | "decimal"): string {
  if (mode === "integer") {
    const digits = raw.replace(/\D/g, "");
    return digits ? formatInputNumber(digits) : "";
  }
  // Keep digits + the first comma (the decimal); grouping dots are re-derived
  // by formatInputNumber, so drop everything else here.
  const cleaned = raw.replace(/[^\d,]/g, "");
  const firstComma = cleaned.indexOf(",");
  const single =
    firstComma === -1
      ? cleaned
      : cleaned.slice(0, firstComma + 1) + cleaned.slice(firstComma + 1).replace(/,/g, "");
  return single ? formatInputNumber(single) : "";
}

/**
 * Canonicalise a free-typed German number into `<grouped>,<decimals>` form.
 *
 * A "." is grouping only when it groups; otherwise the user meant a decimal
 * point. Applied at PARSE time, never while typing — see
 * {@link filterGermanNumberInput} for why that distinction matters.
 *
 *   a "," present   → the "," is the decimal, every "." is grouping
 *   one "." with exactly 3 digits after it → grouping ("15.000" -> 15000)
 *   one "." otherwise → a decimal point ("3.50" -> "3,50", "15." -> "15,")
 *   several "."     → grouping ("1.000.000")
 *
 * "3.500" is genuinely ambiguous and resolves to grouping (3500). A German
 * user meaning three-and-a-half types "3,5".
 */
function canonicalizeGermanNumber(raw: string): string {
  const cleaned = raw.replace(/[^\d.,]/g, "");
  if (!cleaned) return "";
  if (cleaned.includes(",")) {
    const [head, ...rest] = cleaned.replace(/\./g, "").split(",");
    return `${head},${rest.join("")}`;
  }
  const segments = cleaned.split(".");
  return segments.length === 2 && segments[1].length !== 3
    ? `${segments[0]},${segments[1]}`
    : segments.join("");
}

/**
 * Live filter for a number a German user types freely (money, hectares).
 *
 * Keeps digits and the separators the user actually typed; it does NOT decide
 * what a "." means and it does NOT insert grouping of its own. Both of those
 * are deliberate, and the second is not cosmetic:
 *
 * **You cannot disambiguate a "." at the moment it is typed.** "15.0" is both
 * a complete decimal and a prefix of "15.000". A filter that commits to one
 * reading mid-word eats the rest of the number — and if it also inserts its
 * own grouping dot, its own output becomes ambiguous input on the very next
 * keystroke: "1500" → "1.500" → type "0" → "1.5000" → read as a decimal →
 * "1,50". That is a silent 1000× error on every 5-digit price, i.e. on every
 * €/ha ask, produced by the filter that was supposed to prevent it.
 *
 * So: keep what was typed, decide once, at parse time, with
 * {@link parseGermanUserNumber}. The user sees exactly what they entered
 * (which also keeps the caret still), and the value is read as they meant it.
 *
 * @example filterGermanNumberInput("€ 3,50", 2) -> "3,50"
 * @example filterGermanNumberInput("15.000", 2) -> "15.000"   (-> 15000)
 * @example filterGermanNumberInput("3.50", 2)   -> "3.50"     (-> 3.5)
 * @example filterGermanNumberInput("3,5555", 2) -> "3,55"
 * @example filterGermanNumberInput("1.250,5")   -> "1.250,5"  (-> 1250.5)
 */
export function filterGermanNumberInput(raw: string, decimalPlaces?: number): string {
  const cleaned = raw
    .replace(/[^\d.,]/g, "")
    // A number cannot START with a grouping dot; without this the "." in
    // "ca. 3,50" survives as a leading separator (".3,50").
    .replace(/^\.+/, "")
    // A fat-fingered "1..5" is one separator, not two — collapse it so the
    // field never displays a run of dots and the parser reads the intent.
    .replace(/\.{2,}/g, ".");
  const commaIdx = cleaned.indexOf(",");
  if (commaIdx === -1) return cleaned;

  // The first comma is the decimal separator; nothing after it may be a
  // separator any more, and the fraction is capped when a cap is given.
  const head = cleaned.slice(0, commaIdx);
  let tail = cleaned.slice(commaIdx + 1).replace(/[.,]/g, "");
  if (decimalPlaces !== undefined) tail = tail.slice(0, decimalPlaces);
  return `${head},${tail}`;
}

/**
 * Parse a number a user typed, resolving grouping-vs-decimal per
 * {@link canonicalizeGermanNumber}. Returns 0 for anything unusable.
 *
 * Use this for every read of a funnel input — validation gate, API payload and
 * conversion value alike. A value that passes the gate must not be re-read
 * differently at submit time.
 *
 *   "15.000" -> 15000     "3.50"    -> 3.5
 *   "3,50"   -> 3.5       "1.250,5" -> 1250.5
 *   "€ 3,50" -> 3.5       ""        -> 0
 */
export function parseGermanUserNumber(value: string): number {
  return parseGermanDecimal(canonicalizeGermanNumber(value));
}

/**
 * Canonical German money display for a blur handler: whatever the user left in
 * the field becomes "15.000,00" / "3,50".
 *
 * Pairs with {@link filterGermanNumberInput} (which deliberately leaves the
 * typed separators alone) so the field settles into an unambiguous form once
 * the user is done. The output round-trips: it carries a decimal comma, so
 * {@link parseGermanUserNumber} reads every "." in it as grouping.
 *
 * An unparseable value is returned untouched rather than blanked — the caller's
 * validation message should explain it, not silently erase what was typed.
 *
 * @example formatGermanPriceDisplay("15.000") -> "15.000,00"
 * @example formatGermanPriceDisplay("3,5")    -> "3,50"
 * @example formatGermanPriceDisplay("")       -> ""
 */
export function formatGermanPriceDisplay(raw: string, decimalPlaces = 2): string {
  if (!raw.trim()) return "";
  const value = parseGermanUserNumber(raw);
  if (!value) return raw;
  return formatNumber(value, {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

/**
 * Live filter for currency inputs — `filterQuantityInput` plus a hard
 * cap on decimals so "3,5555" can never reach state.
 *
 * @deprecated Superseded by {@link filterGermanNumberInput}. This one folds
 * every "." into ",", which destroys a €/ha price typed with its grouping
 * separator ("15.000" -> "15,00" -> 15). Kept only so the helper's own tests
 * keep documenting that behaviour; no app surface uses it.
 */
export function filterCurrencyInput(raw: string, decimalPlaces: number): string {
  const stripped = filterQuantityInput(raw, "decimal");
  const commaIdx = stripped.indexOf(",");
  if (commaIdx === -1) return stripped;
  const head = stripped.slice(0, commaIdx);
  const tail = stripped.slice(commaIdx + 1).slice(0, decimalPlaces);
  return tail.length > 0 ? `${head},${tail}` : `${head},`;
}

/**
 * Normalise a partially-typed currency value to the canonical "X,YZ"
 * form on blur. "8,5" → "8,50". "8," → "8". "" → "".
 *
 * @deprecated Superseded by {@link formatGermanPriceDisplay}, which is
 * grouping-aware and renders "15.000,00" instead of collapsing the value.
 */
export function formatCurrencyValue(raw: string, decimalPlaces: number): string {
  if (!raw) return "";
  const filtered = filterCurrencyInput(raw, decimalPlaces);
  if (!filtered) return "";
  const commaIdx = filtered.indexOf(",");
  if (commaIdx === -1) return filtered;
  const head = filtered.slice(0, commaIdx) || "0";
  const tail = filtered.slice(commaIdx + 1);
  if (tail.length === 0) return head; // "8," → "8"
  return `${head},${tail.padEnd(decimalPlaces, "0")}`;
}

/**
 * Render a DB number into the canonical German display string suitable
 * for seeding a `<QuantityInput>` or `<CurrencyInput>` on mount.
 * Inverse of `parseGermanQuantity`.
 */
export function formatGermanQuantity(value: number | null, mode: "integer" | "decimal"): string {
  if (value == null) return "";
  if (mode === "integer") return String(Math.trunc(value));
  return String(value).replace(".", ",");
}

/**
 * Render a DB price (float) into the canonical "X,YZ" string for
 * seeding a `<CurrencyInput>`.
 */
export function formatGermanPrice(value: number | null, decimalPlaces = 2): string {
  if (value == null) return "";
  return value.toFixed(decimalPlaces).replace(".", ",");
}

/**
 * Zod refinement that rejects numbers with more than `decimals` decimal
 * places. Used to enforce currency precision on buyer max-price and
 * seller price fields.
 */
export const TWO_DECIMALS_REFINE = (v: number): boolean => Math.round(v * 100) / 100 === v;
export const TWO_DECIMALS_MESSAGE = "Höchstens zwei Nachkommastellen.";

/**
 * Format a price range from string min/max values in German locale.
 *
 * @example formatPriceRange("1.50", "3.00") -> "1,50–3,00 €"
 * @example formatPriceRange("2.00", "2.00") -> "2,00 €"
 * @example formatPriceRange(null, "3.00") -> "3,00 €"
 * @example formatPriceRange(null, null) -> "–"
 */
export function formatPriceRange(min: string | null, max: string | null): string {
  if (!min && !max) return "–";
  const minN = min ? parseFloat(min) : null;
  const maxN = max ? parseFloat(max) : null;
  if (minN && maxN && minN !== maxN) {
    return `${minN.toFixed(2).replace(".", ",")}–${maxN.toFixed(2).replace(".", ",")} €`;
  }
  if (minN) return `${minN.toFixed(2).replace(".", ",")} €`;
  if (maxN) return `${maxN.toFixed(2).replace(".", ",")} €`;
  return "–";
}

export function formatRange<T>(
  min: T | null | undefined,
  max: T | null | undefined,
  formatter: (value: T) => string
): string {
  if (min == null && max == null) return "–";
  if (min == null) return `bis ${formatter(max!)}`;
  if (max == null) return `ab ${formatter(min)}`;
  return `${formatter(min)} – ${formatter(max)}`;
}

/**
 * Minimal user shape accepted by {@link formatUserName}. Every field is
 * optional so it works with partial selections (org member, comment author,
 * deal counterparty, …) — missing fields are simply skipped in the fallback
 * chain.
 */
export interface FormatUserNameInput {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  company_name?: string | null;
}

/**
 * Build a human-readable display name for a user from its
 * {first_name, last_name, email, company_name}.
 *
 * Two ordering modes — both fall through to email, then "Unbekannt":
 *
 * - **default (`preferCompany: false`)** — person first:
 *   `"first last"` → `company_name` → `email` → "Unbekannt".
 *   Use in person-centric surfaces (member/requester lists, comment authors)
 *   or wherever the company is shown on its own line/column.
 *
 * - **`preferCompany: true`** — organisation first:
 *   `company_name` → `"first last"` → `email` → "Unbekannt".
 *   This is what the admin `getUserDisplay` uses (a company is the primary
 *   identity for B2B deal/offering/request rows).
 *
 * @example formatUserName({ first_name: "John", last_name: "Doe" }) -> "John Doe"
 * @example formatUserName({ first_name: "John", company_name: "ACME" }) -> "John"
 * @example formatUserName({ company_name: "ACME", first_name: "John" }, { preferCompany: true }) -> "ACME"
 * @example formatUserName({ email: "u@x.de" }) -> "u@x.de"
 * @example formatUserName(null) -> "Unbekannt"
 */
export function formatUserName(
  user: FormatUserNameInput | null | undefined,
  options?: { preferCompany?: boolean }
): string {
  if (!user) return "Unbekannt";

  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const company = user.company_name || "";

  if (options?.preferCompany) {
    return company || fullName || user.email || "Unbekannt";
  }

  return fullName || company || user.email || "Unbekannt";
}

/**
 * German "updated N hours/days/weeks/months ago" recency label for a preview
 * card timestamp. Returns null for missing/future/invalid input.
 *
 * @example formatRecencyLabel("2026-07-20T09:00:00Z") -> "aktualisiert vor 3 Stunden"
 */
export function formatRecencyLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 24)
    return `aktualisiert vor ${Math.max(1, hours)} ${hours === 1 ? "Stunde" : "Stunden"}`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `aktualisiert vor ${days} ${days === 1 ? "Tag" : "Tagen"}`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `aktualisiert vor ${weeks} ${weeks === 1 ? "Woche" : "Wochen"}`;
  const months = Math.floor(days / 30);
  return `aktualisiert vor ${months} ${months === 1 ? "Monat" : "Monaten"}`;
}

/** Price denomination labels shared by deal and entity displays. */
export function formatPriceUnit(unit: string | null | undefined): string {
  const labels: Record<string, string> = {
    per_point: "€/Punkt",
    per_hectare: "€/ha",
    per_tonne: "€/t",
    per_year: "€/Jahr",
    flat: "Gesamtpreis",
  };
  return unit ? (labels[unit] ?? unit) : "€";
}

/** Labels for stamped deal units and gate messages; unknown units remain visible. */
export function quantityUnitLabel(unitKey: string): string {
  const labels: Record<string, string> = {
    oekopunkte: "Punkte",
    ha: "Hektar",
    objekte: "Objekte",
    lfd_m: "lfd. m",
    stueck: "Stück",
    wertpunkte: "Wertpunkte",
  };
  return labels[unitKey] ?? unitKey;
}

/**
 * The words for a price per unit of a registry unit (ARCH-039 D19: every need is priced per its
 * own unit): "€/Punkt", "€/ha", "€/Objekt", "€/lfd. m", "€/Stück". Unknown units remain visible.
 */
export function pricePerUnitLabel(unitKey: string): string {
  const labels: Record<string, string> = {
    oekopunkte: "€/Punkt",
    wertpunkte: "€/Wertpunkt",
    ha: "€/ha",
    objekte: "€/Objekt",
    lfd_m: "€/lfd. m",
    stueck: "€/Stück",
  };
  return labels[unitKey] ?? `€/${unitKey}`;
}

export function formatQuantityWithUnitKey(qty: number, unitKey: string): string {
  return `${formatNumber(qty)} ${quantityUnitLabel(unitKey)}`;
}

/** The canonical display form of a stored price for seeding an input ("15.000,00", not "15000,00"). */
export function priceInputValue(price: number | null | undefined): string {
  return formatGermanPriceDisplay(formatGermanPrice(price ?? null));
}

export const PRICE_INPUT_ERROR = "Bitte einen Preis größer als 0 eingeben.";

/**
 * A typed price, rounded to cents. Empty input is `null` — whether that is an answer or an
 * omission is the caller's decision. A figure that cannot be read, or is not positive, throws
 * the one message both price fields show. Grouping-aware on purpose: `parseGermanQuantity` read
 * the dot in a "15.000" €/ha ask as a decimal point and saved 15.
 */
export function parsePriceInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const parsed = parseGermanUserNumber(trimmed);
  if (!parsed || parsed <= 0) throw new Error(PRICE_INPUT_ERROR);
  return Math.round(parsed * 100) / 100;
}
